import {
    ChangeDetectorRef,
    Component,
    ComponentRef,
    Input,
    OnInit,
    ViewChild,
    forwardRef,
    inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import {
    FormWrappedControlDirective,
    IWrappedFormComponent,
} from '@wdx/shared/infrastructure/form-framework';
import { WorkflowWrappedControlDirective } from './workflow-wrapped-form-control-host.directive';
import {
    LogicStepParameter,
    LookupObject,
    SystemEntity,
} from '@wdx/shared/swagger-gen';
import { IWrappedWorkflowFormComponentProvider } from '../../interfaces/workflow-wrapped-control.interface';
import { AvatarInfoService } from '@wdx/clmi/api-services/services';
import { WdxDestroyClass } from '@wdx/shared/utils';
import { takeUntil } from 'rxjs';

@Component({
    selector: 'clmi-workflow-wrapped-form-control',
    templateUrl: './workflow-wrapped-form-control.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => WorkflowFormWrappedControlComponent),
            multi: true,
        },
    ],
    standalone: false,
})
export class WorkflowFormWrappedControlComponent
    extends WdxDestroyClass
    implements ControlValueAccessor, OnInit
{
    @Input() parameter: LogicStepParameter;
    @Input() formContext!: any;
    @Input() componentProvider?: IWrappedWorkflowFormComponentProvider;
    @ViewChild(WorkflowWrappedControlDirective, { static: true })
    formWrappedControlHost!: FormWrappedControlDirective;

    private cdr = inject(ChangeDetectorRef);
    private avatarInfo = inject(AvatarInfoService);
    private componentRef!: ComponentRef<IWrappedFormComponent>;

    @Input() label = '';
    @Input() value?: LookupObject;
    @Input() invertedStyle?: boolean;

    inputId?: string;
    onChange: (...args: any[]) => any = () => undefined;
    onTouch: (...args: any[]) => any = () => undefined;

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    writeValue(value: LookupObject) {
        this.value = value;
        if (this.componentRef) {
            this.setLookupValue(value);
        }
    }

    ngOnInit(): void {
        const { component } = this.componentProvider.getComponent();
        if (!this.componentRef) {
            const entityTypes = this.parameter.properties
                .find((p) => p.type === 'SystemEntity')
                ?.options?.map(({ value }) => value);
            const viewContainerRef =
                this.formWrappedControlHost.viewContainerRef;
            this.componentRef = viewContainerRef.createComponent(component);
            this.componentRef.instance.entityTypes = entityTypes;

            this.setLookupValue(this.value);

            this.componentRef.instance.valueChanges.subscribe(
                (val: LookupObject[]) => {
                    this.onChange(val?.[0]);
                },
            );
            this.cdr.markForCheck();
        }
    }

    private setLookupValue(value?: LookupObject) {
        if (!this.componentRef) {
            return;
        }

        const entityType = this.getLookupType();
        const lookup =
            value && typeof value === 'object'
                ? value
                : value && entityType
                  ? ({
                        id: value as unknown as string,
                        lookupType:
                            entityType as unknown as LookupObject['lookupType'],
                    } as LookupObject)
                  : null;

        if (lookup && lookup.lookupType && lookup.id) {
            this.componentRef.instance.setLookupCards([
                {
                    ...lookup,
                    name: lookup.id,
                },
            ]);

            this.avatarInfo
                .getForEntityId(
                    lookup.lookupType as unknown as SystemEntity,
                    lookup.id,
                )
                .pipe(takeUntil(this.destroyed$))
                .subscribe((avatar) => {
                    this.componentRef.instance.setLookupCards([
                        {
                            ...lookup,
                            name: avatar.name,
                        },
                    ]);
                });
        } else {
            this.componentRef.instance.value = null;
        }
    }

    private getLookupType(): SystemEntity | null {
        const typeProperty = this.parameter?.properties?.find(
            (p) => p.type === 'SystemEntity',
        );
        const value = typeProperty?.options?.[0]?.value as SystemEntity;
        return value ?? null;
    }
}
